// Resolves the project/script uuids of the current route for paywall event
// `props` -- separate from `path` (paywall-validation.js's sanitisePath),
// which deliberately strips uuids so it stays low-cardinality and groupable.
// Putting them in props instead means they never touch path's cardinality
// but are still there for drill-down.
//
// route-script-uuid.js (studio/src/common/) was checked first and does not
// fit here: it resolves from React Router's `useParams()`, which only
// answers correctly for a component rendered inside the matched route's own
// element. The upgrade modal is mounted once in AuthorizedLayout as a
// sibling of `<Outlet/>`, not inside it, so `useParams()` there always reads
// the outer (paramless) layout route regardless of which page opened the
// paywall. `currentRouteFeature()` (feature-from-route.js) sits in the same
// spot for the same reason and resolves off `window.location.pathname`
// instead -- this does the same, using the app's own uuid shape (the same
// pattern paywall-validation.js's UUID_SEGMENT already validates path
// segments against).
const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Routes whose *only* possible uuid is a project, never a script:
// `/project/:projectUuid[/:featureName]` (the project hub), the writers
// room's `/shared_project/:projectUuid` (before it picks a feature), and
// `/create-script/:projectUuid[/pilot]/treatment` (the script does not exist
// yet -- there is nothing to be a script uuid). Checked only when exactly one
// uuid segment is present; `/shared_project/:projectUuid/editor/:scriptUuid`
// has two and falls through to the general rule below like everything else.
const PROJECT_ONLY_PREFIXES = ['project', 'shared_project', 'create-script']

// Every other route in the app that carries uuids follows one convention:
// `.../:projectUuid?/:scriptUuid` -- see EditorRoutes.jsx, SharedProjectRoutes.jsx,
// ScriptRoutes.jsx and Budgeting/BoxOffice's routes, all of which put the
// project segment (when present) immediately before the script segment. A
// route with a third uuid after that (script-coverage's `/report/:reportSlug`)
// is a report id, not a project or a script, and is deliberately ignored --
// this resolver only ever answers project_uuid/script_uuid.
export const resolveRouteUuids = (pathname) => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) {
    return { projectUuid: null, scriptUuid: null }
  }

  // Without a project, `/script-coverage/:slug/report/:reportSlug` would read as project + script.
  const allSegments = pathname.split('/').filter(Boolean)
  const reportAt = allSegments.indexOf('report')
  const segments = reportAt === -1 ? allSegments : allSegments.slice(0, reportAt)
  const uuidIndexes = segments.reduce((indexes, segment, index) => {
    if (UUID_SEGMENT_RE.test(segment)) indexes.push(index)
    return indexes
  }, [])

  if (uuidIndexes.length === 0) return { projectUuid: null, scriptUuid: null }

  if (uuidIndexes.length === 1) {
    const uuid = segments[uuidIndexes[0]]
    return PROJECT_ONLY_PREFIXES.includes(segments[0])
      ? { projectUuid: uuid, scriptUuid: null }
      : { projectUuid: null, scriptUuid: uuid }
  }

  return { projectUuid: segments[uuidIndexes[0]], scriptUuid: segments[uuidIndexes[1]] }
}

export const currentRouteUuids = () => {
  // eslint-disable-next-line no-undef
  if (typeof window === 'undefined') return { projectUuid: null, scriptUuid: null }
  // eslint-disable-next-line no-undef
  return resolveRouteUuids(window.location?.pathname)
}

// `path` is resolved centrally (paywall-analytics.js's currentPath) from
// wherever the paywall happened to mount, so its uuids are stripped
// server-side by paywall-validation.js's sanitisePath. `target_path` (the
// destination a blocked navigation was headed to, see show-paywall.js) has
// no server-side equivalent to strip it -- it never reaches path, it is a
// client-built props value -- so it has to be stripped here, on the way in,
// with the same pattern sanitisePath uses: query string dropped, every uuid
// segment replaced with `:uuid`, capped at 200 chars. Kept in lockstep with
// that pattern on purpose -- two paths stripped two different ways would
// make `path` and `target_path` ungroupable against each other.
const UUID_SEGMENT_STRIP_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

export const stripPathUuids = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/')) return null
  return value.split('?')[0].replace(UUID_SEGMENT_STRIP_RE, ':uuid').slice(0, 200)
}
